/*
  accessibility-cart.js — WCAG 2.2 AA remediation (Fix 5)
  Additive only: does NOT modify cart.min.js / product.min.js / app.min.js.

  Responsibilities:
  1. Treat #Cart-Drawer and #Product-Drawer as real dialogs: toggle aria-hidden,
     move focus in on open, trap Tab inside, close on Esc, restore focus on close.
     (WCAG 2.4.3, 2.1.2)
  2. Make the <side-panel-close> element keyboard-operable (Enter/Space). (WCAG 2.1.1)
  3. Announce successful add-to-cart in a polite live region and keep the header
     cart-count screen-reader text in sync. (WCAG 4.1.3 Status Messages)

  All hooks are defensive: originals are always called through, errors never thrown.
*/
(function () {
  'use strict';

  var DRAWER_ID = 'Cart-Drawer';
  var PRODUCT_DRAWER_ID = 'Product-Drawer';
  var ACTIVE_CLASS = 'active';

  var lastFocused = null;

  function drawer() { return document.getElementById(DRAWER_ID); }
  function productDrawer() { return document.getElementById(PRODUCT_DRAWER_ID); }
  function isOpen(el) { return el && el.classList.contains(ACTIVE_CLASS); }

  function liveRegion() { return document.querySelector('[data-cart-live]'); }

  function announce(msg) {
    var live = liveRegion();
    if (!live || !msg) return;
    // Clear first so a repeat of identical text is still re-announced.
    live.textContent = '';
    window.requestAnimationFrame(function () { live.textContent = msg; });
  }

  function focusableWithin(container) {
    if (!container) return [];
    var sel = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),' +
      'textarea:not([disabled]),side-panel-close,[tabindex]:not([tabindex="-1"])';
    return Array.prototype.filter.call(container.querySelectorAll(sel), function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
  }

  function trapTab(e, panel) {
    if (!panel || !isOpen(panel) || e.key !== 'Tab') return;
    var f = focusableWithin(panel);
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  }

  function watchPanel(panelId, titleId) {
    var el = document.getElementById(panelId);
    if (!el) return;
    var wasOpen = isOpen(el);
    var obs = new MutationObserver(function () {
      var nowOpen = isOpen(el);
      if (nowOpen && !wasOpen) openPanel(el, titleId);
      else if (!nowOpen && wasOpen) closePanel(el);
      wasOpen = nowOpen;
    });
    obs.observe(el, { attributes: true, attributeFilter: ['class'] });
  }

  function openPanel(el, titleId) {
    if (!el) return;
    el.setAttribute('aria-hidden', 'false');
    if (document.activeElement && !el.contains(document.activeElement)) {
      lastFocused = document.activeElement;
    }
    var title = (titleId && el.querySelector('#' + titleId)) || el;
    title.setAttribute('tabindex', '-1');
    window.setTimeout(function () { try { title.focus(); } catch (e) {} }, 50);
  }

  function closePanel(el) {
    if (!el) return;
    el.setAttribute('aria-hidden', 'true');
    if (lastFocused && typeof lastFocused.focus === 'function') {
      try { lastFocused.focus(); } catch (e) {}
    }
    lastFocused = null;
  }

  function watchDrawer() {
    watchPanel(DRAWER_ID, 'CartDrawerTitle');
    watchPanel(PRODUCT_DRAWER_ID, 'ProductDrawerTitle');
  }

  // Keyboard: Esc closes, Tab is trapped while a dialog panel is open.
  document.addEventListener('keydown', function (e) {
    var el = drawer();
    var pd = productDrawer();
    var active = (el && isOpen(el)) ? el : (pd && isOpen(pd)) ? pd : null;
    if (!active) return;
    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      var closeBtn = active.querySelector('side-panel-close, .side-panel-close');
      if (closeBtn) closeBtn.click();
      else active.classList.remove(ACTIVE_CLASS);
    } else if (e.key === 'Tab') {
      trapTab(e, active);
    }
  });

  // Make the custom close element behave like a button for the keyboard.
  document.addEventListener('keydown', function (e) {
    var t = e.target;
    if (!t || !t.matches) return;
    if (t.matches('side-panel-close, .side-panel-close') &&
        (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar')) {
      e.preventDefault();
      t.click();
    }
  });

  // Keep the header cart-count SR text in sync after any cart mutation.
  function syncCartCount(count) {
    if (count == null || isNaN(count)) return;
    document.querySelectorAll('[data-cart-count-a11y]').forEach(function (node) {
      node.textContent = count + ' items in cart';
    });
  }

  function handleCartAddResponse(data) {
    try {
      var obj = typeof data === 'string' ? JSON.parse(data) : data;
      if (!obj) return;
      var title = obj.product_title || (obj.items && obj.items[0] && obj.items[0].product_title);
      announce(title ? (title + ' added to your cart.') : 'Item added to your cart.');
      // Refresh the SR count shortly after (cart object updates asynchronously).
      window.setTimeout(refreshCartCount, 400);
    } catch (e) { /* ignore */ }
  }

  function refreshCartCount() {
    try {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (c) { if (c) syncCartCount(c.item_count); })
        .catch(function () {});
    } catch (e) {}
  }

  function isCartAddUrl(url) {
    return typeof url === 'string' && /\/cart\/add(\.js)?(\?|$)/.test(url);
  }

  // --- Wrap fetch (used by Habitat's cart JS and many apps) ---
  if (window.fetch) {
    var origFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var p = origFetch.apply(this, arguments);
      if (isCartAddUrl(url)) {
        p.then(function (res) {
          if (res && res.ok) { res.clone().json().then(handleCartAddResponse).catch(function () {}); }
        }).catch(function () {});
      }
      return p;
    };
  }

  // --- Wrap XHR (covers jQuery $.ajax used by the legacy theme) ---
  if (window.XMLHttpRequest) {
    var origOpen = XMLHttpRequest.prototype.open;
    var origSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__a11yCartAdd = isCartAddUrl(url);
      return origOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      if (this.__a11yCartAdd) {
        this.addEventListener('load', function () {
          if (this.status >= 200 && this.status < 300) {
            handleCartAddResponse(this.responseText);
          }
        });
      }
      return origSend.apply(this, arguments);
    };
  }

  function init() {
    watchDrawer();
    // If a drawer is already open on load (rare), reflect its aria-hidden state.
    var el = drawer();
    if (el && isOpen(el)) openPanel(el, 'CartDrawerTitle');
    var pd = productDrawer();
    if (pd && isOpen(pd)) openPanel(pd, 'ProductDrawerTitle');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
